// Client API tipizzato. In dev passa per il proxy Vite (/api -> http://localhost:5080).
// In prod il sito statico e l'API girano su sottodomini diversi (api.spadhausen.com).

import { withAttribution } from '@/lib/attribution'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export interface Comune {
  egonId: string
  nome: string
  provincia?: string
  cap?: string
}

export interface Via {
  egonId: string
  nome: string
  frazione?: string
  cap?: string
  provincia?: string
}

export interface Civico {
  egonId: string
  numero: string
}

export interface VerifyCoverageRequest {
  comune: Comune
  via: Via
  civico: Civico
}

export type CoverageStato = 'Attivabile' | 'Probabile'

export interface CoverageLinea {
  tecnologia: string
  fornitore: string
  velocitaDownloadMbps: number
  velocitaUploadMbps: number
  stato: CoverageStato
  cabinetDistance?: number
  descrizione?: string
}

export interface CoverageResult {
  coperto: boolean
  linee: CoverageLinea[]
  messaggio?: string
}

export type LeadTipo = 'Richiamo' | 'Contatto'
export type LeadInteresse = 'Casa' | 'Ufficio' | 'Mobile'
export type LeadFasciaOraria = 'Mattina' | 'Pomeriggio' | 'Sera'

export interface LeadAllegatoResult { id: string; fileName: string; dimensione: number }

export interface LeadRequest {
  tipo?: LeadTipo
  nome: string
  telefono: string
  email?: string
  interesse?: LeadInteresse
  fasciaOraria?: LeadFasciaOraria
  messaggio?: string
  consensoPrivacy: boolean
  consensoMarketing?: boolean
  fonte?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  /** consenso alla misurazione pubblicitaria al momento dell'invio (ADR 0030) */
  consensoAds?: boolean
  coupon?: string
  recaptchaToken?: string
  /** riferimenti dei PDF caricati con uploadLeadAllegato */
  allegati?: string[]
}

export interface LeadResult {
  id: number
  stato: string
}

export interface NewsletterRequest {
  email: string
  consensoPrivacy: boolean
  fonte?: string
  recaptchaToken?: string
}

export interface NewsletterResult {
  email: string
  stato: string
  giaIscritto: boolean
}

// ---- Funnel attivazione: ordini / OTP / documenti ----
// I valori enum coincidono con i nomi lato backend (JsonStringEnumConverter).
export type OrderIntentApi = 'Attivazione' | 'Preventivo' | 'Richiamo'
export type OrderSegmentoApi = 'Casa' | 'Business'
export type OrderDocumentTipoApi = 'CartaIdentitaFronte' | 'CartaIdentitaRetro' | 'TesseraSanitaria'
export type OtpChannelApi = 'Sms' | 'Email'

export interface OrderIndirizzo {
  comune?: string
  provincia?: string
  cap?: string
  via?: string
  civico?: string
  frazione?: string
  egonComune?: string
  egonStrada?: string
  egonCivico?: string
}

export interface OrderAddOnLine { categoria: string; nome: string; quantita: number; prezzoMensile: number }
export interface OrderCoverageSnapshot { coperto: boolean; tecnologieAttivabili?: string[]; raccomandata?: string }
export interface OrderConfigurazione {
  offertaTecnologia?: string
  offertaNome?: string
  addOns?: OrderAddOnLine[]
  stimaMensile?: number
  stimaUnaTantum?: number
  copertura?: OrderCoverageSnapshot
  velocita?: string
  rete?: string
  durataMesi?: number
  vincolo?: boolean
  costoAttivazione?: number
}
export interface OrderDocumentRefApi {
  tipo: OrderDocumentTipoApi
  documentId: string
  fileName?: string
  contentType?: string
  dimensione: number
}

export interface SubmitOrderRequest {
  intent: OrderIntentApi
  segmento: OrderSegmentoApi
  tecnologia?: string
  nome?: string
  cognome?: string
  ragioneSociale?: string
  partitaIva?: string
  codiceFiscale?: string
  telefono?: string
  email?: string
  indirizzo?: OrderIndirizzo
  configurazione?: OrderConfigurazione
  documenti?: OrderDocumentRefApi[]
  consensoPrivacy: boolean
  consensoMarketing?: boolean
  telefonoProof?: string
  emailProof?: string
  note?: string
  fonte?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  /** consenso alla misurazione pubblicitaria al momento dell'invio (ADR 0030) */
  consensoAds?: boolean
  coupon?: string
  // Come ci ha conosciuto (dropdown del passo 2). I valori sono i nomi dell'enum backend
  // ComeConosciuto ('Passaparola' | 'RicercaInternet' | 'SuggerimentoAi' | 'Pubblicita' | 'Volantino' | 'Altro').
  comeConosciuto?: string
  // Nome e cognome di chi ha fatto passaparola (solo se comeConosciuto === 'Passaparola').
  segnalatore?: string
  recaptchaToken?: string
}

export interface OrderResult { id: number; stato: string }

export interface OtpRequestBody { channel: OtpChannelApi; target: string; recaptchaToken?: string }
export interface OtpVerifyBody { channel: OtpChannelApi; target: string; code: string }
export interface OtpVerifyResult { verified: boolean; proof?: string }
export interface UploadDocResult {
  documentId: string
  tipo: OrderDocumentTipoApi
  fileName?: string
  contentType?: string
  dimensione: number
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`API ${path} -> ${res.status} ${res.statusText}`)
  }
  return res.json() as Promise<T>
}

async function postJson<TBody, TResult>(path: string, body: TBody): Promise<TResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`API ${path} -> ${res.status} ${res.statusText}`)
  }
  return res.json() as Promise<TResult>
}

// Come postJson, ma su errore prova a estrarre il messaggio del server ({ message })
// per mostrarlo all'utente nel funnel (validazione, anti-spam, OTP, rate-limit).
async function postJsonChecked<TBody, TResult>(path: string, body: TBody): Promise<TResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    let message = `Errore ${res.status}`
    try {
      const j = (await res.json()) as { message?: string }
      if (j?.message) message = j.message
    } catch { /* corpo non JSON */ }
    throw new Error(message)
  }
  return res.json() as Promise<TResult>
}

export const api = {
  searchComuni: (q: string) =>
    getJson<Comune[]>(`/api/v1/coverage/comuni?cerca=${encodeURIComponent(q)}`),

  searchVie: (egonComune: string, q: string) =>
    getJson<Via[]>(`/api/v1/coverage/vie?egonComune=${encodeURIComponent(egonComune)}&cerca=${encodeURIComponent(q)}`),

  searchCivici: (egonVia: string, q: string) =>
    getJson<Civico[]>(`/api/v1/coverage/civici?egonVia=${encodeURIComponent(egonVia)}&cerca=${encodeURIComponent(q)}`),

  verifyCoverage: (req: VerifyCoverageRequest) =>
    postJson<VerifyCoverageRequest, CoverageResult>(`/api/v1/coverage/verify`, req),

  // Invio form "Richiamami gratis" / contatti.
  // Ogni invio porta con sé la provenienza catturata all'arrivo (gclid, utm): vedi lib/attribution.ts.
  submitLead: (req: LeadRequest) =>
    postJson<LeadRequest, LeadResult>(`/api/v1/leads`, withAttribution(req)),

  // Iscrizione newsletter.
  subscribeNewsletter: (req: NewsletterRequest) =>
    postJson<NewsletterRequest, NewsletterResult>(`/api/v1/newsletter`, req),

  // Health "a monte": true se il sottosistema copertura (Egon) è operativo.
  coverageHealth: () =>
    getJson<{ available: boolean }>(`/api/v1/coverage/health`),

  // Funnel /verifica-copertura: invio richiesta (persistenza + email cliente/commerciale).
  submitOrder: (req: SubmitOrderRequest) =>
    postJsonChecked<SubmitOrderRequest, OrderResult>(`/api/v1/orders`, withAttribution(req)),

  // OTP cellulare/email (verifica usa-e-getta).
  requestOtp: (body: OtpRequestBody) =>
    postJsonChecked<OtpRequestBody, { sent: boolean }>(`/api/v1/otp/request`, body),
  verifyOtp: (body: OtpVerifyBody) =>
    postJsonChecked<OtpVerifyBody, OtpVerifyResult>(`/api/v1/otp/verify`, body),

  // Upload documento d'identità (multipart). Ritorna il riferimento da allegare all'ordine.
  uploadOrderDocument: async (
    file: File | Blob,
    tipo: OrderDocumentTipoApi,
    recaptchaToken?: string,
    fileName?: string,
  ): Promise<UploadDocResult> => {
    const fd = new FormData()
    fd.append('file', file, fileName ?? 'documento.jpg')
    fd.append('tipo', tipo)
    if (recaptchaToken) fd.append('recaptchaToken', recaptchaToken)
    const res = await fetch(`${API_BASE}/api/v1/orders/documents`, { method: 'POST', body: fd })
    if (!res.ok) {
      let message = `Errore ${res.status}`
      try {
        const j = (await res.json()) as { message?: string }
        if (j?.message) message = j.message
      } catch { /* noop */ }
      throw new Error(message)
    }
    return res.json() as Promise<UploadDocResult>
  },

  // Upload di un PDF (planimetria) da allegare a un lead: ritorna il riferimento
  // opaco da passare in `allegati` a submitLead. Solo PDF, limiti lato server.
  uploadLeadAllegato: async (file: File, recaptchaToken?: string): Promise<LeadAllegatoResult> => {
    const fd = new FormData()
    fd.append('file', file, file.name)
    if (recaptchaToken) fd.append('recaptchaToken', recaptchaToken)
    const res = await fetch(`${API_BASE}/api/v1/leads/allegati`, { method: 'POST', body: fd })
    if (!res.ok) {
      let message = `Errore ${res.status}`
      try {
        const j = (await res.json()) as { message?: string }
        if (j?.message) message = j.message
      } catch { /* noop */ }
      throw new Error(message)
    }
    return res.json() as Promise<LeadAllegatoResult>
  },
}
